// MYNDIGHETSMATRISEN — hvem KAN beslutte hva, fra nasjonalt nivå til enkeltmann.
//
// Eiers bestilling (2026-07-29): «"Underskrift" for utløsning av eksempelvis
// offensive handlinger er helt unødvendig, men at disse autorisasjonene og "ROE"
// ligger inne som en matrise — fra kongen til Ola enkeltmann. Jeg som Områdesjef
// kan delegeres myndighet til å utføre en del ting. Dette må følge en matrise,
// tett knyttet til ORBAT.»
//
// HVA DETTE ER, OG HVA DET IKKE ER:
//  - Dette er IKKE en ny signaturmekanisme. `fireapproval.ts` svarer på «krever
//    DETTE skuddet godkjenning, og finnes godkjenningen?» — per oppdrag, i
//    øyeblikket. Denne modulen svarer på spørsmålet UNDER det: «har denne
//    enheten MYNDIGHET til å godkjenne/beslutte i det hele tatt?» — stående,
//    på forhånd, sporbart.
//  - Myndighet er enten IBOENDE (nivået har den i kraft av kommandonivå) eller
//    DELEGERT (et høyere nivå har gitt den nedover, med vilkår og evt. utløp).
//    Hver delegasjon er en post som kan revideres — det er loggen som er
//    kontrollen, ikke en underskrift.
//
// TETT KNYTTET TIL ORBAT: delegasjoner adresseres med den KANONISKE
// enhetsadressen (`UnitAddress` fra hierarchy.ts — echelon + territorium +
// kommandosti), samme adresse som ORBAT, ESO-distribusjon og adresseboka
// bruker. Én adressemodell, ingen divergens.
//
// ⚠ TALLENE I KATALOGEN ER POLICY, IKKE KODE-SANNHET. Hvilket nivå som iboende
// har en myndighet, og hvor langt ned den kan delegeres, er EIERS/doktrinens
// avgjørelse. Verdiene her er et forsvarlig utgangspunkt (default deny — heller
// for høyt enn for lavt), lagt i ÉN tabell nettopp for at de skal kunne
// justeres uten å røre logikken. Endrer du en rad, endrer du programmets
// beslutningsrett — gjør det med eier i rommet.
//
// Lagdeling (grunnlov §4): ren logikk, null DOM, null nettverk. At en BRUKER
// faktisk står på nivået hen hevder (rolle→nivå) håndheves i rbac/hub — denne
// modulen resonnerer om NIVÅER og ADRESSER.
import { z } from 'zod';
import { ECHELON_ORDER, ECHELON_LABEL, echelonRank, UnitAddressSchema, addressLabel, } from './hierarchy.js';
/** Kanonisk rekkefølge, laveste → høyeste. Indeks = rang. */
export const NIVAA_ORDER = [...ECHELON_ORDER, 'nasjonal'];
export const MyndighetsNivaaSchema = z.custom((v) => typeof v === 'string' && NIVAA_ORDER.includes(v));
export const NIVAA_LABEL = {
    ...ECHELON_LABEL,
    nasjonal: 'Nasjonal (FOH)',
};
export function nivaaRank(n) {
    return n === 'nasjonal' ? ECHELON_ORDER.length : echelonRank(n);
}
export const KATEGORI_LABEL = {
    ild: 'Ild og våpenbruk',
    beredskap: 'Beredskap og styrkeoppbygging',
    omraade: 'Område og sivilsamfunn',
    informasjon: 'Informasjon og deling',
    roe: 'ROE-profil',
};
/**
 * Katalogen. Rekkefølgen styrer visningen (matrisa grupperes per kategori).
 * `selvforsvar` står først med vilje: den er iboende fra enkeltmann og ALDRI
 * delegerbar — retten til selvforsvar verken gis eller tas i en matrise, og
 * at den står her gjør det synlig i stedet for underforstått.
 */
export const MYNDIGHETER = [
    // Ild og våpenbruk
    {
        id: 'selvforsvar', label: 'Selvforsvar',
        forklaring: 'Retten til å forsvare seg selv og andre mot pågående angrep. Iboende for alle, alltid.',
        kategori: 'ild', iboendeFra: 'enkeltmann', delegerbar: false, lavesteNivaa: 'enkeltmann',
    },
    {
        id: 'ild-offensiv', label: 'Offensiv ildåpning',
        forklaring: 'Beslutte ildåpning ut over selvforsvar (anfall, bakhold, ildoverfall).',
        kategori: 'ild', iboendeFra: 'brigade', delegerbar: true, lavesteNivaa: 'tropp',
    },
    {
        id: 'ildstotte-frigivning', label: 'Frigivning av ildstøtte',
        forklaring: 'Godkjenne indirekte ild (CFF). Danger close/CDE-gaten i kill-chain krever i tillegg sin egen godkjenningspost per oppdrag.',
        kategori: 'ild', iboendeFra: 'kompani', delegerbar: true, lavesteNivaa: 'tropp',
    },
    // Beredskap og styrkeoppbygging
    {
        id: 'beredskapsgrad', label: 'Heving av beredskapsgrad',
        forklaring: 'Endre beredskapsgrad for underlagte avdelinger.',
        kategori: 'beredskap', iboendeFra: 'nasjonal', delegerbar: true, lavesteNivaa: 'brigade',
    },
    {
        id: 'eso-iverksetting', label: 'Iverksette styrkeoppbygging (ESO)',
        forklaring: 'Beordre styrkeoppbygging og fastsette mål/føringer (f.eks. stridsdriller) for underlagte.',
        kategori: 'beredskap', iboendeFra: 'brigade', delegerbar: true, lavesteNivaa: 'kompani',
    },
    {
        id: 'varslingsordre', label: 'Utstede varslingsordre',
        forklaring: 'Utstede VO til underlagte avdelinger (UtS trinn 1).',
        kategori: 'beredskap', iboendeFra: 'brigade', delegerbar: true, lavesteNivaa: 'kompani',
    },
    // Område og sivilsamfunn
    {
        id: 'rekvisisjon', label: 'Rekvisisjon av sivil eiendom',
        forklaring: 'Rekvirere sivil eiendom/materiell etter rekvisisjonsloven.',
        kategori: 'omraade', iboendeFra: 'brigade', delegerbar: true, lavesteNivaa: 'kompani',
    },
    {
        id: 'vegsperring', label: 'Vegsperring / kontrollpost',
        forklaring: 'Etablere sperring eller kontrollpost på veg i eget ansvarsområde.',
        kategori: 'omraade', iboendeFra: 'kompani', delegerbar: true, lavesteNivaa: 'lag',
    },
    {
        id: 'evakuering', label: 'Anbefale evakuering',
        forklaring: 'Anbefale evakuering av sivile overfor politi/kommune. Beslutningen er sivil; anbefalingen er militær.',
        kategori: 'omraade', iboendeFra: 'kompani', delegerbar: true, lavesteNivaa: 'tropp',
    },
    // Informasjon og deling
    {
        id: 'eksport-ekstern', label: 'Eksport ut av systemet',
        forklaring: 'Frigi situasjonsbilde/data ut av SKADE (CoT/NVG, føderering). Speiler eksport-retten i RBAC.',
        kategori: 'informasjon', iboendeFra: 'brigade', delegerbar: true, lavesteNivaa: 'kompani',
    },
    {
        id: 'deling-samvirke', label: 'Deling i sivilt samvirke',
        forklaring: 'Dele operativ informasjon med politi, kommune og sivile samvirkeaktører.',
        kategori: 'informasjon', iboendeFra: 'kompani', delegerbar: true, lavesteNivaa: 'tropp',
    },
    // ROE-profil
    {
        id: 'roe-endring', label: 'Endre ROE-profil',
        forklaring: 'Fastsette eller endre gjeldende engasjementsregler for underlagte.',
        kategori: 'roe', iboendeFra: 'nasjonal', delegerbar: true, lavesteNivaa: 'brigade',
    },
];
export function myndighet(id) {
    return MYNDIGHETER.find((m) => m.id === id);
}
// ── Delegasjonsposten ───────────────────────────────────────────────────────
/**
 * Én delegasjon: et nivå som HAR myndigheten gir den til en KONKRET enhet
 * (ORBAT-adresse), med vilkår i klartekst og valgfritt utløp. Ingen
 * underskrift — sporet av hvem/når/hva ER kontrollen, og hver post skal
 * skrives til hendelsesloggen der den utstedes og der den trekkes.
 */
export const DelegasjonSchema = z.object({
    id: z.string().min(1).max(64),
    myndighetId: z.string().min(1).max(64),
    /** Mottakerenheten — kanonisk ORBAT-adresse. */
    til: UnitAddressSchema,
    /** Vilkår/begrensninger i klartekst («kun i eget AO», «kun objekt X»). */
    vilkaar: z.string().max(300).optional(),
    /** Hvem utstedte (bruker-id/kallesignal) — for loggen, ikke som signatur. */
    utstedtAv: z.string().min(1).max(128),
    /** Nivået utstederen handlet PÅ (verifiseres mot rolle i rbac/hub). */
    utstederNivaa: MyndighetsNivaaSchema,
    tsMs: z.number(),
    utloperMs: z.number().optional(),
    trukketMs: z.number().optional(),
    trukketAv: z.string().max(128).optional(),
});
export function parseDelegasjon(raw) {
    const r = DelegasjonSchema.safeParse(raw);
    return r.success ? r.data : null;
}
/** Er delegasjonen i kraft nå? (ikke trukket, ikke utløpt) */
export function erAktiv(d, naaMs) {
    if (d.trukketMs !== undefined)
        return false;
    if (d.utloperMs !== undefined && naaMs >= d.utloperMs)
        return false;
    return true;
}
/**
 * Default deny, én grunn per avslag:
 *  1. Ukjent myndighet → nei.
 *  2. Ikke delegerbar → nei (selvforsvar kan aldri flyttes).
 *  3. Utsteder har den ikke iboende → nei. (V1-regelen er bevisst streng:
 *     delegert myndighet kan IKKE videredelegeres. Kjeder av delegasjoner er
 *     der revisjonen mister oversikten — vil eier ha det, er det en utvidelse
 *     med egen logg-semantikk, ikke en stille lemping her.)
 *  4. Mottaker under katalogens gulv → nei.
 *  5. Mottaker på/over utsteders nivå → nei (delegasjon går NEDOVER).
 */
export function kanDelegere(myndighetId, utstederNivaa, til) {
    const m = myndighet(myndighetId);
    if (!m)
        return { ok: false, grunn: `Ukjent myndighet: ${myndighetId}` };
    if (!m.delegerbar)
        return { ok: false, grunn: `${m.label} kan ikke delegeres` };
    if (nivaaRank(utstederNivaa) < nivaaRank(m.iboendeFra)) {
        return { ok: false, grunn: `${m.label} er iboende fra ${NIVAA_LABEL[m.iboendeFra]} — ${NIVAA_LABEL[utstederNivaa]} har den ikke å gi (delegert myndighet kan ikke videredelegeres)` };
    }
    if (nivaaRank(til.echelon) < nivaaRank(m.lavesteNivaa)) {
        return { ok: false, grunn: `${m.label} kan ikke delegeres under ${NIVAA_LABEL[m.lavesteNivaa]}` };
    }
    if (nivaaRank(til.echelon) >= nivaaRank(utstederNivaa)) {
        return { ok: false, grunn: 'Delegasjon går nedover — mottaker må stå under utstederens nivå' };
    }
    return { ok: true };
}
/**
 * Treffer delegasjonen denne enheten? EKSAKT adresse: samme echelon og samme
 * kommandosti. En delegasjon til troppen gir ikke laget noe — myndighet gis
 * til et ledd, ikke til alt under det. (At troppssjefen kan beslutte FOR laget
 * sitt er kommando, ikke delegasjon.)
 */
export function gjelderFor(d, enhet) {
    return d.til.echelon === enhet.echelon
        && (d.til.district ?? '') === (enhet.district ?? '')
        && (d.til.area ?? '') === (enhet.area ?? '')
        && (d.til.troop ?? '') === (enhet.troop ?? '')
        && (d.til.team ?? '') === (enhet.team ?? '');
}
export function harMyndighet(myndighetId, enhet, enhetNivaa, delegasjoner, naaMs) {
    const m = myndighet(myndighetId);
    if (!m)
        return { status: 'ingen' };
    if (nivaaRank(enhetNivaa) >= nivaaRank(m.iboendeFra))
        return { status: 'iboende' };
    // Nyeste aktive delegasjon vinner — den er den gjeldende ordren.
    let beste;
    for (const d of delegasjoner) {
        if (d.myndighetId !== myndighetId || !erAktiv(d, naaMs) || !gjelderFor(d, enhet))
            continue;
        if (!beste || d.tsMs > beste.tsMs)
            beste = d;
    }
    return beste ? { status: 'delegert', delegasjon: beste } : { status: 'ingen' };
}
export function matriseCelle(m, nivaa, delegasjoner, naaMs) {
    if (nivaaRank(nivaa) >= nivaaRank(m.iboendeFra))
        return 'iboende';
    const delegertHit = delegasjoner.some((d) => d.myndighetId === m.id && erAktiv(d, naaMs) && d.til.echelon === nivaa);
    if (delegertHit)
        return 'delegert';
    if (m.delegerbar && nivaaRank(nivaa) >= nivaaRank(m.lavesteNivaa))
        return 'delegerbar';
    return 'ingen';
}
/** Antall aktive delegasjoner av `m` til enheter på `nivaa` (til celletall). */
export function antallDelegertTil(m, nivaa, delegasjoner, naaMs) {
    return delegasjoner.filter((d) => d.myndighetId === m.id && erAktiv(d, naaMs) && d.til.echelon === nivaa).length;
}
// ── Tilbaketrekking ─────────────────────────────────────────────────────────
/**
 * Kan `nivaa` trekke delegasjonen? Utstederen selv, eller et HØYERE nivå
 * (sjefen kan alltid trekke det nestkommanderende ga). Scope (at trekkende
 * faktisk står over mottakeren territorielt) håndheves i rbac/hub, som ellers.
 */
export function kanTrekke(d, nivaa) {
    return nivaaRank(nivaa) >= nivaaRank(d.utstederNivaa);
}
/** Trekk en delegasjon — returnerer NY post (immutabelt, posten er historikk). */
export function trekk(d, av, naaMs) {
    return { ...d, trukketMs: naaMs, trukketAv: av };
}
// ── Kill-chain-koblingen ────────────────────────────────────────────────────
/**
 * Overgangen `ready → shot` i kill-chain ER frigivningen av ilden — det
 * irreversible punktet. Alt før (requested/ready) er anmodning og klargjøring;
 * alt etter er konsekvens. Gaten for `ildstotte-frigivning`-myndigheten skal
 * derfor stå på NØYAKTIG denne overgangen, ikke på opprettelsen av målet: å BE
 * om ild (CFF) kan enhver observatør — å FRIGI den krever myndighet.
 * (Egen funksjon, ikke inline i UI-et: at grensen ligger her er doktrine, og
 * doktrine i en klikkhåndterer er usynlig for både test og revisjon.)
 */
export function erIldFrigivning(fra, til) {
    return fra === 'ready' && til === 'shot';
}
// ── Hendelseslogg-tekst (én kilde, så COP og audit sier det samme) ──────────
export function delegasjonsHendelse(d, trukket = false) {
    const m = myndighet(d.myndighetId);
    const navn = m?.label ?? d.myndighetId;
    if (trukket) {
        return {
            tittel: `Myndighet trukket: ${navn}`,
            detalj: `${d.trukketAv ?? '?'} trakk delegasjonen til ${addressLabel(d.til)}`,
        };
    }
    return {
        tittel: `Myndighet delegert: ${navn}`,
        detalj: `${d.utstedtAv} (${NIVAA_LABEL[d.utstederNivaa]}) → ${addressLabel(d.til)}${d.vilkaar ? ` · Vilkår: ${d.vilkaar}` : ''}${d.utloperMs ? ' · tidsbegrenset' : ''}`,
    };
}
//# sourceMappingURL=myndighet.js.map